import type { JSX } from 'react';
import { Icon } from '@/components/Icon';
import { linkURLParts } from './frameDocument';
import { linkWarnings, warningLabel } from './linkRisk';

/** How many selected links the preview lists before it says "and N more". */
const MAX_PREVIEW_URLS = 3;

/**
 * The URL preview (ADR 0053): where the link under the pointer, under keyboard
 * focus or in the selection really goes, as the parent validated it.
 *
 * `aria-hidden`, not a live region: the link it describes is already in the
 * accessibility tree with its own name and, since `prepareFrameLinks`, a
 * `title` of this same URL as its description. A live region would announce a
 * URL every time a mouse crossed a link, which for someone using a screen
 * reader with a pointer is noise on top of what they already hear.
 * `pointer-events: none` in the CSS, so it never takes a click, a tap or a
 * long-press meant for the message under it.
 *
 * A link that looks deceptive from its URL alone (ADR 0101: look-alike
 * characters, a bare IP, `user@` in front of the host, a shortener) gets a
 * short badge under it. Not the text-versus-address check: the preview has no
 * link text, and the confirm, which does, says that one in full.
 */
export function LinkPreview({ urls }: { urls: readonly URL[] }): JSX.Element {
  const shown = urls.slice(0, MAX_PREVIEW_URLS);
  const more = urls.length - shown.length;
  const warning = shown.map((url) => linkWarnings(url)[0]).find((w) => w !== undefined);
  return (
    <div className="message-link-preview" aria-hidden="true">
      <div className="message-link-preview__bar" dir="ltr">
        {shown.map((url) => {
          const parts = linkURLParts(url);
          return (
            <div className="message-link-preview__url" key={url.href}>
              <span className="message-link-preview__scheme">{parts.scheme + parts.userinfo}</span>
              <span className="message-link-preview__host">{parts.host}</span>
              <span className="message-link-preview__rest">{parts.rest}</span>
            </div>
          );
        })}
        {more > 0 && <div className="message-link-preview__more">and {more} more</div>}
        {warning && (
          <div className="message-link-preview__warning">
            <Icon name="alert" size={14} />
            <span>Check this link: {warningLabel(warning)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
